import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { moduleOf, shellQuery } from '../../api/shell'
import { AppsAttentionPill } from '../../ui/AppsAttentionMark'
import { Forbidden, isForbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'
import { appUrl, failed } from '../parts'
import {
  contractText,
  downloadVendorsCsv,
  GRADE_LABEL,
  GRADE_PILL,
  hours,
  pct,
  vendorDetailQuery,
  vendorsQuery,
  type Grade,
  type VendorCaseRow,
  type VendorFigures,
  type VendorRow,
} from './vendorsApi'
import './vendors.css'

/**
 * Overview > Applications > Vendors - furnizorii de suport notati dupa
 * cazurile lor la furnizor, nu dupa ce scrie in contract.
 *
 * Tabelul se sorteaza pe orice coloana (clic pe titlu); clic pe furnizor
 * deschide detaliul lui pe aceeasi pagina: cifrele, aplicatiile (fiecare cu
 * nota ei) si cazurile, cu legatura spre tichet. Perioada (30 / 90 / 365 de
 * zile), firma si furnizorul deschis stau in adresa, ca pagina sa se poata
 * trimite cuiva.
 *
 * Cifrele le calculeaza serverul (`VendorStats`); pagina doar le aseaza.
 * Orele sunt de ceas; termenul depasit e cel al cazului, numarat in
 * programul de suport.
 */
const STYLES = ['css/adm.css']

type SortKey =
  | 'name'
  | 'appCount'
  | 'cases'
  | 'open'
  | 'medianFirstResponseHours'
  | 'avgFirstResponseHours'
  | 'contractHours'
  | 'avgResolutionHours'
  | 'breachedPct'
  | 'escalatedL2'
  | 'grade'

type Column = { key: SortKey; title: string; note?: string }

const COLUMNS: Column[] = [
  { key: 'name', title: 'Vendor' },
  { key: 'appCount', title: 'Apps' },
  { key: 'cases', title: 'Cases' },
  { key: 'open', title: 'Open' },
  { key: 'medianFirstResponseHours', title: 'First response (median)' },
  { key: 'avgFirstResponseHours', title: 'First response (avg)' },
  { key: 'contractHours', title: 'Contract', note: 'response time in the support contract' },
  { key: 'avgResolutionHours', title: 'Resolution (avg)' },
  { key: 'breachedPct', title: 'Deadline missed' },
  { key: 'escalatedL2', title: 'Level 2+' },
  { key: 'grade', title: 'Grade' },
]

/** Nota de la rau la bine, ca "Poor" sa urce primul la sortarea descrescatoare. */
const GRADE_ORDER: Record<Grade, number> = { too_few: 0, good: 1, fair: 2, poor: 3 }

function readUrl(): { days: number; company: number; vendor: string } {
  const q = new URLSearchParams(window.location.search)
  const days = Number(q.get('days'))
  const company = Number(q.get('company'))
  return {
    days: [30, 90, 365].includes(days) ? days : 90,
    company: Number.isFinite(company) && company > 0 ? company : 0,
    vendor: /^[a-z0-9-]{1,190}$/.test(q.get('vendor') ?? '') ? (q.get('vendor') as string) : '',
  }
}

function writeUrl(days: number, company: number, vendor: string) {
  const url = new URL(window.location.href)
  url.searchParams.set('days', String(days))
  if (company > 0) url.searchParams.set('company', String(company))
  else url.searchParams.delete('company')
  if (vendor !== '') url.searchParams.set('vendor', vendor)
  else url.searchParams.delete('vendor')
  window.history.replaceState(null, '', url.toString())
}

export function sortVendors(rows: VendorRow[], key: SortKey, dir: 'asc' | 'desc'): VendorRow[] {
  const value = (r: VendorRow): number | string | null => (key === 'grade' ? GRADE_ORDER[r.grade] : key === 'name' ? r.name.toLowerCase() : r[key])
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const va = value(a)
    const vb = value(b)
    /* Fara cifra (niciun caz raspuns) sta la urma in ambele sensuri. */
    if (va === null && vb === null) return a.name.localeCompare(b.name)
    if (va === null) return 1
    if (vb === null) return -1
    if (va < vb) return -sign
    if (va > vb) return sign
    return a.name.localeCompare(b.name)
  })
}

export function VendorsPage() {
  const shell = useQuery(shellQuery(moduleOf('/applications')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const [state, setState] = useState(readUrl)
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'cases', dir: 'desc' })
  const [busy, setBusy] = useState(false)
  const page = useQuery(vendorsQuery(state.days, state.company))

  const change = (next: Partial<typeof state>) => {
    const merged = { ...state, ...next }
    setState(merged)
    writeUrl(merged.days, merged.company, merged.vendor)
  }

  if (isForbidden(page.error)) return <Forbidden />
  if (page.error) {
    return (
      <Content>
        <div className="alert alert-danger" role="alert" style={{ margin: 20 }}>
          Something went wrong: {page.error.message}
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />
  const data = page.data

  const csv = (vendor?: string) => {
    setBusy(true)
    downloadVendorsCsv(state.days, state.company, vendor)
      .catch((error) => toast(failed(error), 'Vendors', 'error'))
      .finally(() => setBusy(false))
  }

  const order = (key: SortKey) =>
    setSort((s) => ({ key, dir: s.key === key ? (s.dir === 'asc' ? 'desc' : 'asc') : key === 'name' ? 'asc' : 'desc' }))
  const rows = sortVendors(data.vendors, sort.key, sort.dir)

  return (
    <Content>
      <AppsAttentionPill count={shell.data?.appsAttention} />
      <div className="vs-toolbar">
        <a href="/app/applications" className="btn btn-default">
          <i className="fas fa-arrow-left"></i> Applications
        </a>
        <div className="btn-group" role="group" aria-label="Period">
          {(data.periods ?? [30, 90, 365]).map((d) => (
            <button
              key={d}
              type="button"
              id={`vsDays${d}`}
              className={`btn btn-default${state.days === d ? ' active' : ''}`}
              aria-pressed={state.days === d}
              onClick={() => change({ days: d })}
            >
              {d === 365 ? '12 months' : `${d} days`}
            </button>
          ))}
        </div>
        {data.companies.length > 0 && (
          <label className="vs-company">
            Customer{' '}
            <select id="vsCompany" className="form-control input-sm" value={state.company} onChange={(e) => change({ company: Number(e.target.value), vendor: '' })}>
              <option value={0}>All customers</option>
              {data.companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <span className="vs-spacer"></span>
        {data.ready && (
          <button type="button" className="btn btn-default" id="vsCsv" disabled={busy} onClick={() => csv()}>
            <i className="fas fa-file-csv"></i> Export CSV
          </button>
        )}
      </div>

      {!data.ready ? (
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <i className="fas fa-database"></i> {data.message ?? 'Business applications are not set up on this server yet.'}
        </div>
      ) : (
        <>
          <div className="adm-section">
            <h4>Vendors</h4>
            <span className="adm-section-note">
              how support vendors actually answered, from the vendor cases of the last {state.days === 365 ? '12 months' : `${state.days} days`}
            </span>
          </div>
          <div className="adm-card">
            {rows.length === 0 ? (
              <p className="text-muted" style={{ padding: '10px 15px' }} id="vsEmpty">
                No support vendors yet. Set the support vendor on an application, and escalate a ticket to it.
              </p>
            ) : (
              <div className="vs-scroll">
                <table className="table adm-list vs-table" id="vsTable">
                  <thead>
                    <tr>
                      {COLUMNS.map((c) => {
                        const sorted = sort.key === c.key
                        return (
                          <th
                            key={c.key}
                            title={c.note}
                            className={`vs-sortable${sorted ? ` vs-sorted-${sort.dir}` : ''}`}
                            aria-sort={sorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          >
                            <button type="button" className="vs-sort" onClick={() => order(c.key)}>
                              {c.title}
                            </button>
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((v) => (
                      <tr key={v.key} className={state.vendor === v.key ? 'vs-current' : undefined}>
                        <td>
                          <a
                            href={`/app/applications/vendors?vendor=${v.key}&days=${state.days}`}
                            className="vs-vendor"
                            onClick={(e) => {
                              e.preventDefault()
                              change({ vendor: v.key })
                            }}
                          >
                            {v.name}
                          </a>
                          {v.names.length > 1 && <div className="adm-muted small">also written {v.names.filter((n) => n !== v.name).join(', ')}</div>}
                        </td>
                        <td>{v.appCount}</td>
                        <td>{v.cases}</td>
                        <td>{v.open}</td>
                        <FigureCells v={v} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="adm-muted small vs-legend">
            First response and resolution are counted in clock hours from the escalation. A deadline is missed when the vendor answered after the due time
            of the case (counted in their support hours, without holidays), or has not answered and the due time has passed. The grade needs at least{' '}
            {data.minCases} cases: Poor above 30% missed or a median answer more than twice the contract time, Fair above 10% or over the contract time.
          </p>
          {state.vendor !== '' && (
            <VendorDetailPanel vendorKey={state.vendor} days={state.days} company={state.company} busy={busy} onCsv={() => csv(state.vendor)} onClose={() => change({ vendor: '' })} />
          )}
        </>
      )}
    </Content>
  )
}

/** Celulele comune tabelului de furnizori si celui de aplicatii. */
function FigureCells({ v }: { v: VendorFigures }) {
  const slower = v.responseVsContract !== null && v.responseVsContract > 1
  return (
    <>
      <td className={`adm-nowrap${slower ? ' text-danger' : ''}`}>{hours(v.medianFirstResponseHours)}</td>
      <td className="adm-nowrap">{hours(v.avgFirstResponseHours)}</td>
      <td className="adm-nowrap">{contractText(v)}</td>
      <td className="adm-nowrap">{hours(v.avgResolutionHours)}</td>
      <td className="adm-nowrap">
        {pct(v.breachedPct)}
        {v.withDeadline > 0 && (
          <span className="adm-muted small">
            {' '}
            ({v.breached}/{v.withDeadline})
          </span>
        )}
      </td>
      <td className="adm-nowrap">
        {v.escalatedL2}
        {v.escalatedL2Pct !== null && v.escalatedL2 > 0 && <span className="adm-muted small"> ({v.escalatedL2Pct}%)</span>}
      </td>
      <td>
        <GradePill grade={v.grade} />
      </td>
    </>
  )
}

export function GradePill({ grade }: { grade: Grade }) {
  return <span className={`adm-pill ${GRADE_PILL[grade]}`.trim()}>{GRADE_LABEL[grade]}</span>
}

function VendorDetailPanel({
  vendorKey,
  days,
  company,
  busy,
  onCsv,
  onClose,
}: {
  vendorKey: string
  days: number
  company: number
  busy: boolean
  onCsv(): void
  onClose(): void
}) {
  const detail = useQuery({ ...vendorDetailQuery(vendorKey, days, company), retry: false })
  if (detail.error) {
    return (
      <div className="alert alert-info" style={{ margin: '15px 0' }} id="vsNoVendor">
        This vendor has no applications or cases for the chosen customer.{' '}
        <button type="button" className="btn btn-link btn-xs" onClick={onClose}>
          Close
        </button>
      </div>
    )
  }
  if (!detail.data?.vendor) return <Spinner />
  const v = detail.data.vendor
  const apps = detail.data.apps ?? []
  const cases = detail.data.cases ?? []

  return (
    <section id="vsDetail" className="vs-detail">
      <div className="adm-section">
        <h4>{v.name}</h4>
        <span className="adm-section-note">
          {v.appCount} application{v.appCount === 1 ? '' : 's'} at {v.companyCount} customer{v.companyCount === 1 ? '' : 's'}
        </span>
        <span className="vs-spacer"></span>
        <button type="button" className="btn btn-default btn-xs" id="vsDetailCsv" disabled={busy} onClick={onCsv}>
          <i className="fas fa-file-csv"></i> Cases CSV
        </button>
        <button type="button" className="btn btn-default btn-xs" id="vsDetailClose" onClick={onClose}>
          <i className="fas fa-times"></i> Close
        </button>
      </div>
      <div className="vs-tiles">
        <Tile label="Grade" value={<GradePill grade={v.grade} />} />
        <Tile label="Cases" value={`${v.cases}`} note={`${v.open} open`} />
        <Tile
          label="First response"
          value={hours(v.medianFirstResponseHours)}
          note={`median; contract ${contractText(v)}`}
          bad={v.responseVsContract !== null && v.responseVsContract > 1}
        />
        <Tile label="Resolution" value={hours(v.avgResolutionHours)} note={`average, ${v.resolved} resolved`} />
        <Tile label="Deadline missed" value={pct(v.breachedPct)} note={`${v.breached} of ${v.withDeadline} with a deadline`} bad={(v.breachedPct ?? 0) > 10} />
        <Tile label="Escalated to level 2+" value={`${v.escalatedL2}`} note={v.escalatedL2Pct === null ? '' : `${v.escalatedL2Pct}% of cases`} />
      </div>

      <h5 className="vs-sub">Applications</h5>
      <div className="adm-card">
        {apps.length === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            The applications of this vendor were removed; their cases still count.
          </p>
        ) : (
          <div className="vs-scroll">
            <table className="table adm-list" id="vsApps">
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Customer</th>
                  <th>Cases</th>
                  <th>First response (median)</th>
                  <th>First response (avg)</th>
                  <th>Contract</th>
                  <th>Resolution (avg)</th>
                  <th>Deadline missed</th>
                  <th>Level 2+</th>
                  <th>Grade</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <a href={appUrl(a.id)} target="_blank" rel="noopener noreferrer">{a.name}</a>
                    </td>
                    <td>{a.companyName}</td>
                    <td>{a.cases}</td>
                    <FigureCells v={a} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <h5 className="vs-sub">
        Cases
        {(detail.data.casesShown ?? 0) < v.cases && <span className="adm-muted small"> - the newest {detail.data.casesShown}, the CSV has them all</span>}
      </h5>
      <div className="adm-card">
        {cases.length === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            No vendor cases in this period.
          </p>
        ) : (
          <div className="vs-scroll">
            <table className="table adm-list" id="vsCases">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Application</th>
                  <th>Opened</th>
                  <th>Status</th>
                  <th>Level</th>
                  <th>First response</th>
                  <th>Resolution</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <CaseRow key={c.id} c={c} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

function CaseRow({ c }: { c: VendorCaseRow }) {
  return (
    <tr className={c.breached ? 'vs-breached' : undefined}>
      <td>
        <a href={`/app/easeedesk/ticket/${c.ticketId}`} target="_blank" rel="noopener noreferrer">#{c.ticketId}</a>
        {c.vendorRef !== '' && <strong> {c.vendorRef}</strong>}
      </td>
      <td>
        {c.appName}
        <div className="adm-muted small">{c.companyName}</div>
      </td>
      <td className="adm-nowrap">{c.createdAt}</td>
      <td>
        <span className={`adm-pill ${c.breached ? 'adm-pill-bad' : c.open ? 'adm-pill-warn' : 'adm-pill-ok'}`}>{c.statusLabel}</span>
      </td>
      <td>{c.level}</td>
      <td className="adm-nowrap">
        {hours(c.firstResponseHours)}
        {c.breached && <strong className="text-danger"> - deadline missed</strong>}
      </td>
      <td className="adm-nowrap">{hours(c.resolutionHours)}</td>
    </tr>
  )
}

function Tile({ label, value, note, bad }: { label: string; value: ReactNode; note?: string; bad?: boolean }) {
  return (
    <div className={`vs-tile${bad ? ' vs-tile-bad' : ''}`}>
      <div className="vs-tile-label">{label}</div>
      <div className="vs-tile-value">{value}</div>
      {note ? <div className="adm-muted small">{note}</div> : null}
    </div>
  )
}
